/**
 * Unified holdings aggregation.
 *
 * The redesigned home screen shows one list of every asset the account holds
 * across all wallets and chains, sorted by fiat value, rather than a card per
 * wallet address. This framework-free module is the load-bearing core of that
 * view: it flattens balances.byWalletId across all wallets into a single sorted
 * array of holding rows, derives the account-level twenty-four-hour change that
 * the store does not carry pre-computed, and provides the small formatting and
 * labeling helpers the new components share.
 *
 * It imports nothing from React, mirroring lib/network, so the aggregation is a
 * set of pure, unit-testable functions that any caller can memoize.
 *
 * Testnet safety: a wallet showing real value when it is testnet is a hazard, so
 * the caller filters the wallet set to the active tier before aggregating (see
 * lib/network). This module treats every row it is given as in-scope and never
 * mixes tiers on its own.
 */

import {
  chainFamily,
  effectiveEnvironment,
  lineMatchesActiveTier,
  type ChainFamily,
  type NetworkInfo,
} from './network';
import type { BalanceLine, WalletBalances, WalletSummary } from './types';
import { filterHomeNetwork, type HomeNetwork } from './homePreferences';

/**
 * Human-readable chain display names keyed by the gateway chain key. Chains not
 * in the table fall back to a title-cased version of the key itself, so a new
 * chain still reads sensibly before it is added here. Extracted here so the
 * aggregation and the new components share one source of chain labels.
 */
export const CHAIN_LABELS: Record<string, string> = {
  ethereum: 'Ethereum',
  sepolia: 'Sepolia',
  bitcoin: 'Bitcoin',
  'bitcoin-taproot': 'Bitcoin Taproot',
  solana: 'Solana',
  'solana-testnet': 'Solana Testnet',
  'solana-devnet': 'Solana Devnet',
  sui: 'Sui',
  'sui-testnet': 'Sui Testnet',
  'sui-devnet': 'Sui Devnet',
  bsc: 'BNB Smart Chain',
  'bsc-testnet': 'BNB Testnet',
  base: 'Base',
  'base-sepolia': 'Base Sepolia',
  hyperevm: 'Hyperliquid',
  'hyperevm-testnet': 'Hyperliquid Testnet',
  tron: 'Tron',
  'tron-nile': 'Tron Nile',
  evm: 'EVM',
};

/**
 * Resolve a chain key to a display name, title-casing an unknown key so the UI
 * degrades gracefully for a chain not yet in the table.
 *
 * @param chain Gateway chain key, or null when unknown.
 * @param displayName Optional upstream label, preserved for other networks.
 * @returns A human-readable chain name.
 */
export function chainLabel(chain: string | null, displayName?: string): string {
  // Keep the public network name consistent even with legacy API labels.
  // The registry keys remain unchanged for filtering, icons and requests.
  if (chain === 'hyperevm' || chain === 'hyperevm-testnet') return CHAIN_LABELS[chain];
  if (displayName) return displayName;
  if (chain && CHAIN_LABELS[chain]) return CHAIN_LABELS[chain];
  const key = chain ?? '';
  return key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Unknown chain';
}

/**
 * A small map from a coin symbol to its full asset name. Balance lines carry
 * only a symbol, so the name shown as the primary text on a holding row comes
 * from this table, falling back to the symbol itself for anything unlisted.
 */
const SYMBOL_NAMES: Record<string, string> = {
  BTC: 'Bitcoin',
  ETH: 'Ethereum',
  WETH: 'Wrapped Ether',
  BNB: 'BNB',
  WBNB: 'Wrapped BNB',
  SOL: 'Solana',
  TRX: 'Tron',
  SUI: 'Sui',
  USDT: 'Tether USD',
  USDC: 'USD Coin',
};

/**
 * The subset of EVM coin symbols whose native network can be inferred from the
 * symbol alone. Unified EVM wallets flatten every network's holdings into one
 * list without a per-line network key, so a faithful per-network subtitle like
 * the mockup's "BNB Smart Chain" versus "Ethereum" is only derivable for coins
 * whose symbol names their network. Everything else falls back to the EVM
 * family label. See the aggregation notes below.
 */
const EVM_SYMBOL_CHAIN: Record<string, string> = {
  ETH: 'ethereum',
  BNB: 'bsc',
  WBNB: 'bsc',
};

/**
 * Resolve the muted network subtitle for one holding row. When the line carries a
 * concrete network from the gateway's unified response, its display name is the
 * subtitle, so a Base or Hyperliquid line reads its real chain. For a flat wallet
 * the subtitle is the wallet's concrete chain label. For a unified EVM line with
 * no network, the subtitle is derived from the coin symbol when the symbol names
 * its network (ETH, BNB) and falls back to the generic EVM label otherwise.
 *
 * @param line The balance line.
 * @param chain The owning view's chain key.
 * @returns A human-readable network subtitle.
 */
export function networkSubtitle(line: BalanceLine, chain: string): string {
  // A line that knows its concrete network names its real chain, which the symbol
  // alone cannot recover for a stablecoin like USDC shared across networks.
  if (line.network) return chainLabel(line.network.chainKey, line.network.displayName);
  if (chain === 'evm') {
    const mapped = EVM_SYMBOL_CHAIN[line.symbol.toUpperCase()];
    if (mapped) return chainLabel(mapped);
    return chainLabel('evm');
  }
  return chainLabel(chain);
}

/**
 * The badge chain key to overlay on a holding row's coin icon. When the line
 * carries a concrete network, that chain is the badge, so a Base USDC row shows
 * the Base mark. For a flat wallet the badge is the concrete chain. For a unified
 * EVM line with no network the badge is derived from the symbol when known (so a
 * BNB row shows the BNB mark) and falls back to the generic EVM chain, which
 * resolves to the Ethereum mark.
 *
 * @param line The balance line.
 * @param chain The owning view's chain key.
 * @returns A chain key suitable for CoinIcon's chain resolver.
 */
export function badgeChain(line: BalanceLine, chain: string): string {
  if (line.network) return line.network.chainKey;
  if (chain === 'evm') {
    return EVM_SYMBOL_CHAIN[line.symbol.toUpperCase()] ?? 'evm';
  }
  return chain;
}

/**
 * The concrete chain key a holding row carries, feeding the network filter, the
 * top-level chain grouping, and walletDisplayChain. When the line knows its
 * concrete network, that chain is used, so a filter on the row groups it under
 * its real chain rather than the generic EVM view chain. Otherwise the owning
 * view's chain key is kept.
 *
 * @param line The balance line.
 * @param chain The owning view's chain key.
 * @returns The concrete chain key for the row.
 */
export function rowChain(line: BalanceLine, chain: string): string {
  return line.network ? line.network.chainKey : chain;
}

/**
 * The identity of one asset choice.
 *
 * A SYMBOL IS NOT A KEY. A unified EVM account holds ETH on Ethereum and ETH on
 * Base at the same address, so two choices share the symbol "ETH" and differ
 * only by chain. Keying the selector on the symbol alone gave both options the
 * same value, which made them indistinguishable to the select element and to
 * every lookup: picking either resolved to whichever came first, so the form
 * showed the wrong balance and, worse, carried the wrong chain into the draft
 * the send is built from.
 *
 * @param asset The choice to identify.
 * @returns A key unique across chains.
 */
export function assetKey(asset: { symbol: string; chain: string }): string {
  return `${asset.chain}:${asset.symbol}`;
}

/** One asset row in the unified holdings list, flattened across all wallets. */
export interface HoldingRow {
  /** Token symbol, for example "USDT". */
  symbol: string;
  /** Full asset name for the primary line, for example "Tether USD". */
  name: string;
  /** Muted network subtitle, for example "Tron" or "BNB Smart Chain". */
  network: string;
  /** The owning view's chain key, feeding chainFamily for the filter. */
  chain: string;
  /** Explicit imported-token network, preserving its existing chain/action identity. */
  networkChainKey?: string;
  /** The chain key used to pick the small badge overlay on the icon. */
  badge: string;
  /** Native token amount as a string, for example "27.42". */
  native: string;
  /** USD value of the line, or null when unpriced. Sort key and bold value. */
  fiat: number | null;
  /** USD unit price of one token, or null when unpriced. Shown by the asset picker. */
  price: number | null;
  /** Twenty-four-hour percent change, or null when unavailable. */
  change24h: number | null;
  /** The wallet this holding belongs to, for the account filter and routing. */
  walletId: string;
  /** The per-wallet address the balance was read against. */
  address: string;
  /**
   * Whether the token is on the curated allow-list. A curated balance is
   * verified; an imported custom token is not, so an unverified row badges as
   * such in the list. Curated rows built by the aggregation carry the balance
   * line's own flag, which is true for the curated catalog the gateway returns.
   */
  verified: boolean;
  /**
   * Whether the row is its network's native coin rather than a token. Carried
   * from the balance line so the send flow can tell a Base ETH row from a Base
   * USDC row after a unified EVM wallet's networks have been flattened together.
   */
  isNative: boolean;
}

/**
 * The list of balance lines a wallet view contributes to the aggregate. A flat
 * wallet contributes its real native coin (skipped for the synthetic empty
 * native that unified EVM wallets carry) followed by its tokens. A unified EVM
 * wallet's native is the synthetic empty line, so it is skipped and every
 * holding is already present in tokens. This mirrors the existing card body and
 * the Send asset picker so the same rows appear everywhere.
 *
 * When deployment info is supplied, a unified EVM wallet's lines are narrowed to
 * the active tier: the gateway flattens every served EVM network into one list
 * spanning both tiers, so a Sepolia line and an Ethereum line arrive under the
 * same wallet, and only the per-line network key can tell them apart. A non-EVM
 * flat wallet line carries no per-line network, and its whole wallet the
 * wallet-level gate already scoped, so it is always kept. A unified EVM line that
 * the gateway left untagged is kept in testnet mode but dropped in mainnet mode,
 * because such a line could be a testnet EVM line and must not leak in as a
 * mainnet amount (see lineMatchesActiveTier). Omitting info keeps every line, for
 * callers that have already scoped their input.
 *
 * @param view The mapped balances view for one wallet.
 * @param info Deployment signals for the per-line tier filter, or undefined to
 *   keep every line.
 * @returns The balance lines to render, native first when real.
 */
function viewLines(view: WalletBalances, info?: NetworkInfo): BalanceLine[] {
  const lines: BalanceLine[] = [];
  if (view.native.symbol !== '') lines.push(view.native);
  const all = lines.concat(view.tokens);
  if (!info) return all;
  // Pass the owning view's chain so the per-line gate can tell a unified EVM
  // wallet (whose untagged lines are dropped in mainnet mode) from a flat wallet
  // (whose lines the wallet-level gate already scoped and are always kept).
  return all.filter((line) => lineMatchesActiveTier(line, info, view.chain));
}

/**
 * Flatten balances.byWalletId across every wallet into one array of holding
 * rows, sorted by fiat value descending. Wallets without a loaded view, wallets
 * whose key is not finalized, and lines that carry a read error are skipped so
 * the list only shows readable holdings. Priced rows lead the sort; unpriced
 * rows (fiat null) sort last but are retained, since the dust rule below is what
 * hides truly empty rows behind the show-all affordance.
 *
 * The caller passes the wallet set already narrowed to the active tier, plus the
 * balances map, so this function never mixes testnet and mainnet value at the
 * wallet level. When deployment info is supplied it also narrows each wallet's
 * lines to the active tier, which the wallet-level filter cannot do for a unified
 * EVM wallet: that one wallet is genuinely on both tiers, and the gateway returns
 * its lines from every served EVM network in one list. Passing info drops the
 * off-tier EVM lines here, at the single choke point that feeds both the rows and
 * portfolioTotals, so the shown rows and the summed total exclude them together.
 * Non-EVM flat wallet lines carry no per-line network and are always kept, so
 * their separation stays governed by the wallet-level gate exactly as before.
 *
 * @param wallets The wallet summaries in scope, already tier-filtered.
 * @param byWalletId Balances keyed by wallet id.
 * @param info Deployment signals for the per-line EVM tier filter, or undefined to
 *   keep every line (for a caller that has already scoped its lines).
 * @returns Holding rows sorted by fiat value descending.
 */
export function aggregateHoldings(
  wallets: WalletSummary[],
  byWalletId: Record<string, WalletBalances>,
  info?: NetworkInfo,
): HoldingRow[] {
  const rows: HoldingRow[] = [];

  for (const wallet of wallets) {
    const view = byWalletId[wallet.id];
    if (!view || view.pending) continue;

    for (const line of viewLines(view, info)) {
      // A line that failed to read carries no trustworthy value, so it is
      // dropped from the aggregate rather than shown as a misleading zero. This
      // mirrors the Send asset picker and the per-line Unavailable state.
      if (line.error) continue;

      rows.push({
        symbol: line.symbol,
        name: SYMBOL_NAMES[line.symbol.toUpperCase()] ?? line.symbol,
        network: networkSubtitle(line, view.chain),
        chain: rowChain(line, view.chain),
        badge: badgeChain(line, view.chain),
        native: line.amount,
        fiat: line.usdValue,
        price: line.usdPrice,
        change24h: line.change24h,
        walletId: wallet.id,
        address: view.address,
        verified: line.verified,
        // A flat wallet's native line is the view's own native coin; a unified
        // EVM wallet's lines carry the flag stamped at mapping time, since its
        // synthetic top-level native is skipped entirely by viewLines.
        isNative: line.isNative ?? line === view.native,
      });
    }
  }

  // Fiat descending, treating an unpriced row as the lowest value so priced
  // rows always lead. The subtraction is stable enough for this list size.
  rows.sort((a, b) => (b.fiat ?? -Infinity) - (a.fiat ?? -Infinity));
  return rows;
}

/** The fiat threshold below which a row is treated as dust and hidden by default. */
export const DUST_THRESHOLD_USD = 0.01;

/**
 * Whether a holding row is dust, meaning it is hidden until the show-all
 * affordance is used. A priced row is dust when its value is under one cent,
 * matching the mockup's "under $0.01" copy. An unpriced row splits by trust: a
 * verified (curated) asset with a positive amount is a genuine holding the user
 * funded, so it stays visible even though it contributes no fiat figure. This
 * is what keeps a testnet balance, which has no market price, from vanishing
 * from the list. An unpriced row that is unverified (an imported custom token)
 * or holds nothing remains dust.
 *
 * @param row The holding row.
 * @returns True when the row should be hidden by default.
 */
export function isDust(row: HoldingRow): boolean {
  if (row.fiat != null) return row.fiat < DUST_THRESHOLD_USD;
  return !(row.verified && Number(row.native) > 0);
}

/** The account-level totals shown in the portfolio header. */
export interface PortfolioTotals {
  /** Sum of priced USD across the rows. */
  totalUsd: number;
  /** Derived twenty-four-hour percent change, or null when nothing priced. */
  changePct: number | null;
  /** Derived twenty-four-hour dollar delta, or null when nothing priced. */
  changeUsd: number | null;
}

/**
 * Derive the account-level twenty-four-hour change from per-line values. The
 * store carries a percent change per line but no aggregate, so the prior value
 * of each priced line is reconstructed as usdValue / (1 + change / 100), the
 * priors are summed, and the aggregate change is the difference against the
 * current total. Lines with an unknown change are treated as flat, so they move
 * neither the total nor the delta. A change of exactly minus one hundred percent
 * would divide by zero and is treated as flat as well.
 *
 * When nothing is priced the change is null rather than zero, so the header can
 * omit a misleading zero-percent chip.
 *
 * @param rows The holding rows to total.
 * @returns The summed fiat total and the derived change, or null change.
 */
export function portfolioTotals(rows: HoldingRow[]): PortfolioTotals {
  let totalNow = 0;
  let totalPrior = 0;
  let anyPriced = false;

  for (const row of rows) {
    if (row.fiat == null || !Number.isFinite(row.fiat)) continue;
    anyPriced = true;
    totalNow += row.fiat;

    const pct = row.change24h;
    // Treat an unknown change or the divide-by-zero minus-one-hundred case as
    // flat, so the line's prior value equals its current value.
    if (pct == null || !Number.isFinite(pct) || pct <= -100) {
      totalPrior += row.fiat;
    } else {
      totalPrior += row.fiat / (1 + pct / 100);
    }
  }

  if (!anyPriced || totalPrior <= 0) {
    return { totalUsd: totalNow, changePct: null, changeUsd: null };
  }

  const changeUsd = totalNow - totalPrior;
  const changePct = (changeUsd / totalPrior) * 100;
  return { totalUsd: totalNow, changePct, changeUsd };
}

/**
 * Format a fiat amount as a localized USD string. Null and non-finite values
 * render as a dash placeholder so an unpriced total does not show a misleading
 * zero.
 *
 * @param value USD value, or null when unpriced.
 * @returns A currency string, or a dash placeholder.
 */
export function formatUsd(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '--';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    // Render the plain dollar sign rather than the locale's disambiguated "US$"
    // form, so a USD figure reads as "$1,234.56" across locales.
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a signed fiat delta with a leading arrow and its absolute magnitude,
 * for example "▲ $142.60". Null renders as a dash so an unknown delta does not
 * show a false zero.
 *
 * @param value USD delta, or null when unknown.
 * @returns An arrow-prefixed currency string, or a dash.
 */
export function formatSignedUsd(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '--';
  const arrow = value >= 0 ? '▲' : '▼';
  const magnitude = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    // The plain dollar sign, matching formatUsd, so a delta reads "▲ $142.60".
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 2,
  }).format(Math.abs(value));
  return `${arrow} ${magnitude}`;
}

/**
 * Format a token amount for display, trimming to a readable precision. Small
 * balances keep more fraction digits so dust stays distinguishable from zero
 * without crowding large numbers.
 *
 * @param amount Human-readable decimal balance as a string.
 * @returns A trimmed display string.
 */
export function formatAmount(amount: string): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  if (n === 0) return '0';
  const fractionDigits = n < 1 ? 6 : 4;
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: fractionDigits,
  }).format(n);
}

/**
 * Format a twenty-four-hour percent change with an arrow and one decimal place,
 * for example "▲1.2%" or "▼1.1%". A zero change reads "0.0%" with no arrow.
 * Null renders as null so the caller can omit the element.
 *
 * @param change Percent change, or null.
 * @returns An arrow-prefixed percentage string, or null.
 */
export function formatChangePct(change: number | null | undefined): string | null {
  if (change == null || !Number.isFinite(change)) return null;
  if (change === 0) return '0.0%';
  const arrow = change > 0 ? '▲' : '▼';
  return `${arrow}${Math.abs(change).toFixed(1)}%`;
}

/**
 * Shorten a long address to a head and tail with an ellipsis, leaving short
 * addresses untouched.
 *
 * @param address The full address.
 * @returns A shortened address for display.
 */
export function shortenAddress(address: string): string {
  if (address.length <= 16) return address;
  return `${address.slice(0, 8)}...${address.slice(-6)}`;
}

/**
 * A stable emoji avatar for a wallet identity, derived from its chain family so
 * a given wallet always shows the same glyph. The account list uses an emoji
 * rather than a gradient monogram, matching the mockup's plant and chain marks.
 * Unknown families fall back to a neutral coin glyph.
 *
 * @param chain The wallet's chain key, or null.
 * @returns A single emoji character.
 */
export function walletEmoji(chain: string | null): string {
  const family: ChainFamily = chainFamily(chain);
  switch (family) {
    case 'Solana':
      return '◎'; // A ringed mark echoing the Solana glyph.
    case 'EVM':
      return '♦'; // A diamond echoing the Ethereum mark.
    case 'Tron':
      return '▲'; // A triangle echoing the Tron mark.
    case 'Bitcoin':
      return '฿'; // The baht sign, visually close to the Bitcoin mark.
    case 'Sui':
      return '💧'; // A water drop echoing the Sui mark.
    default:
      return '🪙'; // A coin, for an unclassified wallet.
  }
}

/**
 * Infer a concrete chain key from an on-chain address by its format alone. The
 * wallet list endpoint omits the address, so this reads the address off the
 * balances view when neither the view's chain nor the summary's chain is set.
 * The checks run from the most distinctive format to the least so a Solana
 * base58 string, which overlaps the widest character set, is only matched once
 * the sharper prefixes have been ruled out. Returns null when nothing matches.
 *
 * @param address The chain-native address to classify.
 * @returns A concrete chain key the icon and family maps understand, or null.
 */
function inferChainFromAddress(address: string): string | null {
  const addr = address.trim();
  if (!addr) return null;

  // An 0x-prefixed hex address is EVM at 42 characters (20-byte account) and Sui
  // at 66 characters (32-byte object id), so the length disambiguates the two.
  if (addr.startsWith('0x')) {
    if (addr.length === 42) return 'evm';
    if (addr.length === 66) return 'sui';
    return null;
  }

  // Bitcoin bech32 forms come first because 'tb1' and 'bc1' are unambiguous and
  // longer than the legacy single-character prefixes checked below.
  const lower = addr.toLowerCase();
  if (lower.startsWith('bcrt1') || lower.startsWith('bc1') || lower.startsWith('tb1')) {
    return 'bitcoin';
  }

  // A base58 Tron address is exactly 34 characters and always starts with 'T',
  // which no other supported chain shares, so it is matched before the legacy
  // Bitcoin prefixes that also live in the base58 alphabet.
  if (addr.startsWith('T') && addr.length === 34) return 'tron';

  // Legacy and testnet Bitcoin addresses start with a fixed set of characters:
  // '1' and '3' on mainnet, and '2', 'm', or 'n' on testnet and regtest.
  if (/^[132mn]/.test(addr)) return 'bitcoin';

  // Anything left that is a plausible base58 string of a Solana public key's
  // length is treated as Solana, the broadest bucket, checked last.
  if (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(addr)) return 'solana';

  return null;
}

/**
 * Resolve the concrete chain key for a wallet, preferring the most reliable
 * signal available. The balances-slice view carries the chain the holdings list
 * already trusts, so it wins. The wallet summary's chain is the next fallback,
 * though the list endpoint leaves it null for many wallets. When neither is
 * present the chain is inferred from the address format on the view, since the
 * list endpoint omits the summary's address.
 *
 * The returned key is one the existing chainFamily grouping and the CoinIcon
 * chain map understand, for example 'evm', 'sui', 'tron', 'bitcoin', or
 * 'solana'. Null means the chain is genuinely unknown, so the caller can fall
 * back to a neutral avatar rather than guessing.
 *
 * @param wallet The wallet summary from the list.
 * @param view The balances view for the wallet, when loaded.
 * @returns A concrete chain key, or null when nothing identifies it.
 */
export function resolveWalletChain(
  wallet: WalletSummary,
  view: WalletBalances | undefined,
): string | null {
  // The balances view chain is the same source the holdings list trusts.
  if (view?.chain) return view.chain;
  // The summary chain is often null on the list endpoint, but honor it when set.
  if (wallet.chain) return wallet.chain;
  // Last resort: infer from the address the view was read against.
  if (view?.address) return inferChainFromAddress(view.address);
  return null;
}

/**
 * The summed USD a wallet holds on the active tier. For a flat wallet this equals
 * the view's own total, since every line is on the wallet's single tier. For a
 * unified EVM wallet, whose gateway total sums both tiers, this re-sums only the
 * active-tier lines so a testnet-mode session never shows a mainnet subtotal as
 * the wallet's balance. Lines with a read error contribute nothing, matching the
 * aggregate. Returns null when nothing on the active tier is priced, so the caller
 * can render a dash rather than a misleading zero.
 *
 * @param view The balances view for one wallet.
 * @param info Deployment signals for the per-line EVM tier filter, or undefined to
 *   sum every priced line.
 * @returns The active-tier USD total, or null when nothing priced.
 */
export function walletTierTotal(
  view: WalletBalances,
  info?: NetworkInfo,
): number | null {
  let total = 0;
  let anyPriced = false;
  for (const line of viewLines(view, info)) {
    if (line.error) continue;
    const fiat = line.usdValue;
    if (fiat == null || !Number.isFinite(fiat)) continue;
    anyPriced = true;
    total += fiat;
  }
  return anyPriced ? total : null;
}

/** A concrete display chain for a wallet: a chain key plus its human label. */
export interface WalletDisplayChain {
  /** A concrete chain key the CoinIcon and chainFamily maps understand. */
  chainKey: string;
  /** The chain display name, for example "BNB Smart Chain" or "Bitcoin". */
  label: string;
}

/**
 * The concrete chain a wallet is shown under in the account switcher's chain
 * sections. A non-EVM wallet resolves to its one concrete chain (bitcoin, tron,
 * solana, sui) through resolveWalletChain. A unified EVM wallet holds several
 * networks under one address, so it is placed under the concrete EVM network
 * where it holds the most fiat value: each priced balance line is attributed to
 * its network with the same symbol-to-network mapping the holdings rows use
 * (a BNB line implies BNB Smart Chain, an ETH line implies Ethereum), the fiat
 * is summed per network, and the largest wins. A unified EVM wallet with no
 * priced EVM balance defaults to Ethereum so it still lands in a real section.
 *
 * When deployment info is supplied, a unified EVM wallet is placed under a network
 * on the active tier only, so a testnet-mode session lands it in a testnet EVM
 * section and never under a mainnet network whose lines the session is hiding.
 *
 * @param wallet The wallet summary from the list.
 * @param view The balances view for the wallet, when loaded.
 * @param info Deployment signals for the per-line EVM tier filter, or undefined.
 * @returns A concrete chain key and its display label.
 */
/**
 * The priced fiat a wallet holds on each concrete network.
 *
 * Shared by {@link walletDisplayChain} and {@link walletNetworks} so the network
 * a wallet is *shown* under and the networks it can be *filtered* by are derived
 * from one tally rather than two that can disagree.
 *
 * A line whose badge falls back to the generic 'evm' key names no network and is
 * skipped: it cannot decide a section, and it cannot answer "does this wallet
 * hold anything on Base". Unpriced lines are skipped for the same reason -- there
 * is no value to rank -- which is a real limitation, not an oversight: a wallet
 * holding only unpriced tokens tallies as empty.
 *
 * @param wallet The wallet whose lines are tallied.
 * @param view Its balances view, or undefined before one loads.
 * @param info Deployment signals; narrows the lines to the active tier.
 * @returns Fiat per concrete network key. Empty when nothing priced is held.
 */
function networkFiat(
  wallet: WalletSummary,
  view: WalletBalances | undefined,
  info?: NetworkInfo,
): Map<string, number> {
  const chainKey = view?.chain ?? resolveWalletChain(wallet, view) ?? 'evm';
  const byNetwork = new Map<string, number>();
  if (!view) return byNetwork;
  for (const line of viewLines(view, info)) {
    if (line.error) continue;
    const fiat = line.usdValue;
    if (fiat == null || !Number.isFinite(fiat)) continue;
    // STRICTLY POSITIVE. A line priced at exactly 0 is a network the wallet is
    // present on but holds nothing on, and the gateway returns one for every EVM
    // network it tracks. Counting those made "the networks this wallet holds on"
    // mean "every network that exists" -- every EVM wallet reported the same set,
    // including one with no balance at all, which tells the user nothing.
    if (fiat <= 0) continue;
    const network = badgeChain(line, chainKey);
    if (network === 'evm') continue;
    byNetwork.set(network, (byNetwork.get(network) ?? 0) + fiat);
  }
  return byNetwork;
}

/**
 * The canonical display order for concrete networks.
 *
 * Deliberately fixed rather than sorted by value. A wallet's badges and the
 * filter chips both read from this, and ordering either by fiat would make them
 * rearrange whenever a balance moved -- the same restlessness that made the old
 * single chain logo unusable as an identity.
 */
export const NETWORK_DISPLAY_ORDER = [
  'bitcoin',
  'ethereum',
  'bsc',
  'base',
  'hyperevm',
  'tron',
  'solana',
  'sui',
];

/**
 * Every concrete network belonging to one family, in canonical order.
 *
 * A property of the family itself, not of anything a wallet holds. The section
 * heading names a family, and these name what that family spans -- true whether
 * the wallets under it hold anything or not, which is why an empty Tron account
 * still shows the Tron mark and an EVM section shows every EVM network rather
 * than only the ones currently funded.
 *
 * Keyed off the mainnet identifiers because the MARK is what is being shown and
 * a testnet shares its mainnet's mark: base-sepolia is drawn with Base's icon.
 *
 * @param family The chain family to describe.
 * @returns Concrete chain keys in canonical order.
 */
export function familyNetworks(family: ChainFamily): string[] {
  return NETWORK_DISPLAY_ORDER.filter((chainKey) => chainFamily(chainKey) === family);
}

/** The rank a concrete network sorts at; unknown keys last, stable among themselves. */
export function networkRank(chainKey: string): number {
  const index = NETWORK_DISPLAY_ORDER.indexOf(chainKey);
  return index === -1 ? NETWORK_DISPLAY_ORDER.length : index;
}

/**
 * Every concrete network a wallet actually holds priced value on, plus the
 * network it is displayed under.
 *
 * This is what a network filter should match, rather than the single display
 * chain. A unified EVM account holds one address across every EVM network, so
 * asking "which network is this wallet on" has no answer -- but "does it hold
 * anything on Base" does. Filtering on the display chain answered the first
 * question and therefore hid a wallet with real Base holdings whenever some
 * other network happened to hold more.
 *
 * The display chain is always included so a wallet always matches the section it
 * is rendered in, including the `ethereum` fallback an empty wallet takes.
 *
 * @param wallet The wallet to describe.
 * @param view Its balances view, or undefined before one loads.
 * @param info Deployment signals; narrows to the active tier.
 * @returns Concrete chain keys, deduplicated, in canonical display order.
 */
export function walletNetworks(
  wallet: WalletSummary,
  view: WalletBalances | undefined,
  info?: NetworkInfo,
): string[] {
  const networks = new Set(networkFiat(wallet, view, info).keys());
  networks.add(walletDisplayChain(wallet, view, info).chainKey);
  // Canonical order, not fiat order: these are rendered as the wallet's badges,
  // and badges that reshuffle when a balance moves are the problem this whole
  // path was fixing.
  return Array.from(networks).sort((a, b) => networkRank(a) - networkRank(b));
}

/**
 * The heading a wallet is grouped under: its chain FAMILY, not a network.
 *
 * A family is a property of the account itself and never changes. The concrete
 * network is not: it is resolved from whichever network currently holds the most
 * fiat, so grouping by it made a wallet migrate between headings when a balance
 * moved, and filed a wallet holding nothing under `ethereum` purely as a
 * fallback. One EVM account is one wallet on every EVM network at once, so
 * "ETHEREUM" and "BASE" were never categories it could belong to.
 *
 * @param family The wallet's chain family.
 * @returns The section heading.
 */
export function familyLabel(family: ChainFamily): string {
  return family === 'Other' ? 'Other' : family;
}

export function walletDisplayChain(
  wallet: WalletSummary,
  view: WalletBalances | undefined,
  info?: NetworkInfo,
): WalletDisplayChain {
  const resolved = resolveWalletChain(wallet, view);

  // A concrete non-EVM chain is already the display chain. Only the unified EVM
  // key (and a chain that classifies EVM but is not concrete) needs a network
  // picked from the balances below.
  if (resolved && resolved !== 'evm' && chainFamily(resolved) !== 'EVM') {
    return { chainKey: resolved, label: chainLabel(resolved) };
  }

  // For an EVM wallet, attribute each priced line to its concrete network using
  // the same badge derivation the holdings rows use, then keep the network with
  // the most fiat. A concrete EVM view chain (for example 'ethereum') attributes
  // every line to itself; the unified 'evm' view splits per line by symbol. The
  // lines are already narrowed to the active tier when info is supplied, so the
  // winning network is one on the active tier.
  const byNetwork = networkFiat(wallet, view, info);

  let best: string | null = null;
  let bestFiat = -Infinity;
  for (const [network, fiat] of byNetwork) {
    if (fiat > bestFiat) {
      best = network;
      bestFiat = fiat;
    }
  }

  // Default to a representative network when no priced EVM balance names one, so
  // the wallet still lands in a concrete section rather than a generic EVM bucket.
  // The default follows the active tier when info is supplied, so a testnet-mode
  // session defaults a value-less unified EVM wallet into the Sepolia section
  // rather than the mainnet Ethereum one it is otherwise hiding.
  const fallback =
    info && effectiveEnvironment(info) === 'testnet' ? 'sepolia' : 'ethereum';
  const picked = best ?? fallback;
  return { chainKey: picked, label: chainLabel(picked) };
}

/**
 * One chain summary row for the top level of the home drilldown. Rather than one
 * line per asset across every wallet, which shows a user with several wallets on
 * the same chain the same asset twice, the top level shows one row per concrete
 * chain the account holds. Tapping a row drills into that chain family.
 */
export interface ChainSummary {
  /**
   * The chain family the row filters to when tapped, feeding the network filter.
   * Several concrete chains can share one family (for example Ethereum and BNB
   * Smart Chain are both EVM), which is why the row also keeps a concrete key.
   */
  family: ChainFamily;
  /**
   * A representative concrete chain key for the row, used to pick the logo and
   * the display name. For a split EVM view this is the concrete network (for
   * example "bsc") so the row shows the real chain mark, not a generic one.
   */
  chainKey: string;
  /** The chain display name, for example "Ethereum" or "BNB Smart Chain". */
  name: string;
  /** How many of the account's wallets hold value on this chain. */
  walletCount: number;
  /** Summed USD across every holding on this chain, or null when none is priced. */
  fiat: number | null;
  /** The value-weighted twenty-four-hour percent change, or null when unpriced. */
  change24h: number | null;
}

/**
 * The concrete chain key a holding row groups under at the top level. A row
 * carries both the owning view chain and a per-line badge chain; the badge is
 * the more concrete of the two on a unified EVM view (a BNB line badges "bsc",
 * an ETH line "ethereum"), so grouping by the badge splits Ethereum and BNB
 * Smart Chain into their own summary rows rather than collapsing them into one
 * generic EVM row. A flat wallet's badge equals its chain, so this is a no-op
 * there. This keeps the summary honest about which real chain holds the value.
 *
 * @param row The holding row to key.
 * @returns The concrete chain key the row groups under.
 */
function summaryChainKey(row: HoldingRow): string {
  // The badge is the concrete per-line chain; fall back to the view chain when a
  // row carries no badge, which never happens for the aggregated rows but keeps
  // the helper total.
  return row.badge || row.chain;
}

/**
 * Group flattened holding rows into one summary per concrete chain, summing the
 * fiat and deriving a value-weighted twenty-four-hour change so a large holding
 * moves the chain figure more than a tiny one. Rows are grouped by their
 * concrete chain key, so a user with two Ethereum wallets sees one Ethereum
 * summary row rather than two, and the same asset never appears twice.
 *
 * The wallet count is the number of distinct wallets contributing any row to the
 * chain, which is what the "N wallets" subline reports. The weighted change is
 * reconstructed the same way portfolioTotals derives the account change: each
 * priced line's prior value is its current value divided by one plus its
 * percent change, the priors are summed per chain, and the change is the
 * difference against the chain total. Unpriced lines and the divide-by-zero
 * minus-one-hundred case are treated as flat.
 *
 * The result is sorted by fiat descending, with a chain that has no priced value
 * ordered last, matching the asset list's own ordering.
 *
 * @param rows The flattened holding rows for the account, already tier-scoped.
 * @returns One summary per concrete chain, sorted by fiat descending.
 */
export function aggregateByChain(rows: HoldingRow[]): ChainSummary[] {
  interface Accumulator {
    family: ChainFamily;
    chainKey: string;
    wallets: Set<string>;
    totalNow: number;
    totalPrior: number;
    anyPriced: boolean;
  }

  const byChain = new Map<string, Accumulator>();

  for (const row of rows) {
    const chainKey = summaryChainKey(row);
    let acc = byChain.get(chainKey);
    if (!acc) {
      acc = {
        family: chainFamily(chainKey),
        chainKey,
        wallets: new Set<string>(),
        totalNow: 0,
        totalPrior: 0,
        anyPriced: false,
      };
      byChain.set(chainKey, acc);
    }

    // Every wallet that contributes any row to the chain counts toward the
    // "N wallets" subline, whether or not the row is priced.
    acc.wallets.add(row.walletId);

    if (row.fiat == null || !Number.isFinite(row.fiat)) continue;
    acc.anyPriced = true;
    acc.totalNow += row.fiat;

    const pct = row.change24h;
    // An unknown change or the divide-by-zero minus-one-hundred case is flat, so
    // the line's prior value equals its current value and it moves nothing.
    if (pct == null || !Number.isFinite(pct) || pct <= -100) {
      acc.totalPrior += row.fiat;
    } else {
      acc.totalPrior += row.fiat / (1 + pct / 100);
    }
  }

  const summaries: ChainSummary[] = [];
  for (const acc of byChain.values()) {
    let change24h: number | null = null;
    if (acc.anyPriced && acc.totalPrior > 0) {
      change24h = ((acc.totalNow - acc.totalPrior) / acc.totalPrior) * 100;
    }
    summaries.push({
      family: acc.family,
      chainKey: acc.chainKey,
      name: chainLabel(acc.chainKey),
      walletCount: acc.wallets.size,
      fiat: acc.anyPriced ? acc.totalNow : null,
      change24h,
    });
  }

  // Fiat descending, with an unpriced chain ordered last so priced chains lead.
  summaries.sort((a, b) => (b.fiat ?? -Infinity) - (a.fiat ?? -Infinity));
  return summaries;
}

/**
 * One wallet summary row for the middle level of the home drilldown, shown when
 * a chain family is filtered but no single wallet is selected. Each row stands
 * for one wallet on that chain, so a user with two Ethereum wallets sees both
 * and can pick which one to drill into.
 */
export interface WalletChainSummary {
  /** The wallet id, for selecting it as the home and the Send/Receive source. */
  walletId: string;
  /** The wallet's human-readable label. */
  label: string;
  /** The resolved concrete chain key, feeding the chain logo. */
  chainKey: string;
  /** The wallet's on-chain address, or an empty string when still pending. */
  address: string;
  /** The wallet's summed USD across its holdings, or null when none is priced. */
  fiat: number | null;
  /**
   * A short native summary, for example "1.42 ETH", or an empty string when the
   * wallet has no readable native line. This is the "small native summary" the
   * middle level shows beside the fiat balance.
   */
  nativeSummary: string;
}

/**
 * Build one summary row per wallet whose holdings fall in the given chain
 * family, for the middle level of the drilldown. A wallet is included when any
 * of its rows classify into the family, so a unified EVM wallet holding only BNB
 * still appears under the EVM family. The fiat is the sum of that wallet's rows
 * within the family, and the native summary is the wallet's own native coin when
 * it carries one.
 *
 * Rows are re-derived from the aggregate so the same tier scoping and
 * error-dropping the asset list applies here too, including the per-line EVM tier
 * filter when deployment info is supplied, so a unified EVM wallet's off-tier
 * networks do not add to its family fiat. The result is sorted by fiat descending
 * so the largest wallet leads.
 *
 * @param wallets The wallet summaries in scope, already tier-filtered.
 * @param byWalletId Balances keyed by wallet id.
 * @param family The chain family to narrow to.
 * @param info Deployment signals for the per-line EVM tier filter, or undefined.
 * @returns One summary per wallet in the family, sorted by fiat descending.
 */
export function walletsForFamily(
  wallets: WalletSummary[],
  byWalletId: Record<string, WalletBalances>,
  family: ChainFamily,
  info?: NetworkInfo,
  homeNetwork: HomeNetwork | null = null,
): WalletChainSummary[] {
  const summaries: WalletChainSummary[] = [];

  for (const wallet of wallets) {
    const view = byWalletId[wallet.id];
    if (!view || view.pending) continue;

    // Rows for just this wallet, filtered to the family. Reusing aggregateHoldings
    // keeps the family classification, the error dropping, the per-line EVM tier
    // filter, and the native/token split identical to what the asset list shows.
    const rows = filterHomeNetwork(aggregateHoldings([wallet], byWalletId, info), homeNetwork).filter(
      (row) => chainFamily(row.chain) === family,
    );
    if (rows.length === 0) continue;

    let totalNow = 0;
    let anyPriced = false;
    for (const row of rows) {
      if (row.fiat == null || !Number.isFinite(row.fiat)) continue;
      anyPriced = true;
      totalNow += row.fiat;
    }

    // The native summary is the wallet's real native coin. A unified EVM view
    // carries a synthetic empty native (symbol ''), so it has no single native
    // line and the summary is left blank rather than inventing one.
    const nativeSummary =
      view.native.symbol !== ''
        ? `${formatAmount(view.native.amount)} ${view.native.symbol}`.trim()
        : '';

    summaries.push({
      walletId: wallet.id,
      label: wallet.label || 'Wallet',
      chainKey: resolveWalletChain(wallet, view) ?? view.chain,
      address: view.address || wallet.address || '',
      fiat: anyPriced ? totalNow : null,
      nativeSummary,
    });
  }

  summaries.sort((a, b) => (b.fiat ?? -Infinity) - (a.fiat ?? -Infinity));
  return summaries;
}
